import React, { useState, useCallback } from 'react';
import { useForm } from 'react-final-form';
import { SelectChangeEvent } from '@mui/material';

import { Select } from '@occc-app/design-system';
import { Cpg } from '@occc/graphql';

import { ChangeCpgModal } from './change-cpg.modal';

import {
  ScanDataSubmissionGroupsUpdateFormValues,
  ScanDataSubmissionGroupsUpdateFormValuesPaths,
} from '../scan-data-submission-groups-update.types';
import { tobaccoCompanyOptions } from '../scan-data-submission-groups-update.constants';

const getCpgLabel = (cpg: Cpg): string => {
  const option = tobaccoCompanyOptions.find((opt) => opt.value === cpg);
  return option?.label || cpg;
};

export type TobaccoCompanyWithConfirmationProps = {
  currentCpg: Cpg;
  cpgWasChanged: boolean;
};

export const TobaccoCompanyWithConfirmation = ({
  currentCpg,
  cpgWasChanged,
}: TobaccoCompanyWithConfirmationProps) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingCpg, setPendingCpg] = useState<Cpg | null>(null);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const form = useForm<ScanDataSubmissionGroupsUpdateFormValues>();

  const skipConfirmation = cpgWasChanged || hasConfirmed;

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newValue = event.target.value as Cpg;

      if (skipConfirmation || newValue === currentCpg) {
        return;
      }

      setPendingCpg(newValue);
      setShowModal(true);
    },
    [skipConfirmation, currentCpg],
  );

  const handleConfirm = useCallback(() => {
    if (!pendingCpg) {
      setShowModal(false);
      setPendingCpg(null);
      return;
    }

    form.change('tobaccoCompany', pendingCpg);
    setHasConfirmed(true);
    setShowModal(false);
    setPendingCpg(null);
  }, [form, pendingCpg]);

  const handleCancel = useCallback(() => {
    form.change('tobaccoCompany', currentCpg);
    setShowModal(false);
    setPendingCpg(null);
  }, [form, currentCpg]);

  return (
    <>
      <Select
        name={'tobaccoCompany' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths}
        label='Tobacco Company'
        data={tobaccoCompanyOptions}
        formControlProps={{ size: 'medium' }}
        onChange={handleChange}
      />
      <ChangeCpgModal
        show={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        fromCpgName={getCpgLabel(currentCpg)}
        toCpgName={pendingCpg ? getCpgLabel(pendingCpg) : ''}
      />
    </>
  );
};
